// The staff "Prequalification" tab, shared by the Broker and Brokerage detail
// pages. ONE implementation parameterised by owner kind — the presentation (site
// card, controls, the three lists, empty state) is identical on both sides and
// the two thin wrappers below differ only in which read they issue and which
// owner-specific extras they render.
//
// SHAPE. What is PINNED above the sub-tabs is what identifies the site and acts
// on it: the inherited banner, the site card with its own Edit / Unpublish /
// Delete / Copy link, and the inherited create-or-confirm row. Everything that
// renders a LIST sits behind the sub-tab bar — Completions · Invitations ·
// In progress — and only the active panel mounts, which is the whole point of
// the tab bar: the three lists used to co-mount and fire three reads on every
// arrival regardless of which one the staff member had come to look at.
//
// `MemberOwnSites` deliberately renders BELOW the tab strip, as a footer. It is
// about OTHER records, not this one's lists, and pinning it would push the tabs
// down the page on every brokerage.
//
// This tab's own reads are gated Brokers.Read / Brokerages.Read — the same
// permission that opens the record. The two list panels behind it are NOT: they
// call endpoints gated Prequalification.Read, which far fewer roles hold, so
// their sub-tabs are hidden rather than rendered into a 403. See SiteWorkspace.
//
// Creating and configuring a prequalification site remains Phoenix-INTERNAL:
// every control here is behind a staff-held permission, and no partner-facing
// surface can reach any of it.
//
// NOTHING LOADS UNTIL THE TAB IS CLICKED. The detail pages render this component
// only for the active tab. Do not add a prefetch, a badge, or a count on the tab
// label — the absence of a pre-click signal is deliberate, and no unmounted
// sub-tab knows its own total anyway (D8).

import { useEffect, useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Copy, Globe, Pencil, Trash2, UserPlus } from "lucide-react";
import { Badge, Button, EmptyState, Tabs } from "@/design-system/components/ui";
import { ConfirmModal } from "@/components/ui/PromptModals";
import { useToast } from "@/components/ui/Toast";
import { useDateFormatter } from "@/utils/datetime";
import { useAuth } from "@/hooks/useAuth";
import {
  CreatePrequalSiteButton,
  type PrequalSiteLaunchContext,
} from "@/components/partners/CreatePrequalSiteButton";
import { PartnerBrandWizardModal } from "@/components/partners/PartnerBrandWizardModal";
import { PrequalSiteCompletionsPanel } from "@/components/partners/PrequalSiteCompletionsPanel";
import { PrequalSiteInvitesSection } from "@/components/partners/PrequalSiteInvitesSection";
import { PrequalSiteDraftsSection } from "@/components/partners/PrequalSiteDraftsSection";
import { SendPrequalInviteModal } from "@/components/prequalification/SendPrequalInviteModal";
import {
  useBrokerPrequalSite,
  useBrokeragePrequalSite,
  useBuyerTeamPrequalSite,
} from "@/hooks/partners/usePrequalSiteAdmin";
import {
  invalidatePartnerBrandAndSiteReads,
  useDeletePartnerBrand,
  usePartnerBrandDeleteEligibility,
  useSetPartnerBrandActive,
} from "@/hooks/partners/usePartnerBrands";
import { useConfirmPrequalSiteInheritance } from "@/hooks/partners/usePrequalSiteInherit";
import { useAdvertisements } from "@/hooks/buyers/useAdvertising";
import { extractErrorMessage } from "@/api/client";
import type {
  BrokerageMemberOwnSite,
  PrequalSiteCard,
  PrequalSiteCompletionRow,
} from "@/api/prequalSiteAdmin";

export type PrequalSiteTabOwner =
  | { kind: "broker"; id: string; name: string }
  | { kind: "brokerage"; id: string; name: string }
  /** A Phoenix BUYER TEAM (D8). The only owner whose completions enter the
   *  Buyer CRM — see BuyerTeamPanel. */
  | { kind: "buyerTeam"; id: string; name: string };

/** The three lists, in the order they appear. Tab ids ARE the `?view=` values,
 *  so there is no second vocabulary to keep in step. */
const VIEW_TABS = [
  { id: "completions", label: "Completions" },
  { id: "invitations", label: "Invitations" },
  { id: "in-progress", label: "In progress" },
] as const;

type PrequalSiteView = (typeof VIEW_TABS)[number]["id"];

export function PrequalificationSiteTab({ owner }: { owner: PrequalSiteTabOwner }) {
  switch (owner.kind) {
    case "broker":
      return <BrokerPanel brokerId={owner.id} brokerName={owner.name} />;
    case "brokerage":
      return <BrokeragePanel brokerageId={owner.id} brokerageName={owner.name} />;
    case "buyerTeam":
      return <BuyerTeamPanel buyerTeamId={owner.id} teamName={owner.name} />;
  }
}

// ── Broker ───────────────────────────────────────────────────────────────────

function BrokerPanel({ brokerId, brokerName }: { brokerId: string; brokerName: string }) {
  const query = useBrokerPrequalSite(brokerId);
  const { date } = useDateFormatter();
  // Held HERE, above the site conditional, because creating a site flips that
  // conditional while the wizard is still open — see CreateSiteWizardMount.
  const [launch, setLaunch] = useState<PrequalSiteLaunchContext | null>(null);

  if (query.isLoading) return <LoadingLine />;
  if (query.isError || !query.data) return <ErrorLine error={query.error} />;

  const data = query.data;
  const site = data.site;
  const inherited = data.resolution === "Inherited";
  const firmName = data.inheritedFromBrokerageName ?? "this brokerage";

  // Both broker launch positions — the empty state, and the create-or-confirm
  // row on an inherited site — name the SAME owner, so it is authored once.
  const launchForThisBroker = () =>
    setLaunch({ ownerBrokerId: brokerId, ownerBrokerName: brokerName });

  // On an INHERITED site every list below belongs to the FIRM, because the
  // resolved brand is the firm's. There is no per-broker attribution on a
  // submission, an invite or a draft, and none is invented — so each list says
  // whose it is rather than letting a reader take it for this broker's own work.
  const sharedDraftsCaption = inherited
    ? `These in-progress applications are shared across all brokers at ${firmName}. They are not attributed to this broker.`
    : null;
  const sharedCompletionsCaption = inherited
    ? `These completions are shared across all brokers at ${firmName}. They are not attributed to this broker.`
    : null;

  return (
    <div className="grid gap-space-4" data-testid="prequal-site-tab">
      {site ? (
        <>
          {inherited && (
            <div
              className="rounded-lg border border-border-light bg-white-warm p-space-3 text-small"
              data-testid="prequal-site-inherited-banner"
            >
              <div className="flex items-center gap-space-2">
                <Building2 size={14} strokeWidth={1.5} aria-hidden />
                <span>
                  Inherits{" "}
                  {data.inheritedFromBrokerageId ? (
                    <Link
                      to={`/brokerages/${data.inheritedFromBrokerageId}`}
                      className="text-gold-shadow hover:underline"
                    >
                      {data.inheritedFromBrokerageName ?? "their brokerage"}
                    </Link>
                  ) : (
                    (data.inheritedFromBrokerageName ?? "their brokerage")
                  )}
                  &rsquo;s prequalification site.
                </span>
              </div>
              <p className="mt-space-1 mb-0 text-caption text-gray-mid">
                This broker owns no site of their own. They can be given one — it
                would replace the inherited site for this broker only.
              </p>
              {data.inheritConfirmedAt && (
                <p className="mt-space-1 mb-0 text-caption text-gray-mid">
                  Inheritance reviewed {date(data.inheritConfirmedAt)}.
                </p>
              )}
            </div>
          )}

          <SiteCardBlock site={site} />

          {inherited && (
            <div className="flex flex-wrap items-center gap-space-3">
              {/* 🔴 This row is INSIDE the inherited branch, so giving the broker
                  their own site unmounts it: resolution goes Inherited → Own and
                  `inherited` flips false. The wizard therefore cannot live here
                  — it is mounted below, outside every branch. */}
              <CreatePrequalSiteButton onLaunch={launchForThisBroker} />
              <ConfirmInheritanceAction
                brokerId={brokerId}
                alreadyConfirmedAt={data.inheritConfirmedAt ?? null}
              />
            </div>
          )}

          <SiteWorkspace
            ownerKind="broker"
            ownerId={brokerId}
            brandId={site.brandId}
            seedRows={data.completions}
            seedTotal={data.completionTotal}
            completionsCaption={sharedCompletionsCaption}
            draftsCaption={sharedDraftsCaption}
            // 🔴 The inherited attribution. Carried over from the retired
            // in-person launcher and CORRECTED: it used to say the completion was
            // credited to "this broker" on every site, which on an inherited one
            // is false — the resolved brand is the firm's, so the firm is who it
            // is credited to.
            attribution={
              inherited
                ? `“${site.displayName}” is ${firmName}’s site, so a prequalification started here is credited to the firm — not to this broker.`
                : `“${site.displayName}” is pre-selected, so the completion is credited to this broker.`
            }
            creditedTo="this broker"
          />
        </>
      ) : (
        <NoSiteState
          ambiguousFirm={data.resolution === "AmbiguousFirm"}
          subject="broker"
          create={<CreatePrequalSiteButton onLaunch={launchForThisBroker} />}
        />
      )}

      <CreateSiteWizardMount launch={launch} onClose={() => setLaunch(null)} />
    </div>
  );
}

/**
 * The liaison's "I have reviewed which site this broker uses" stamp.
 *
 * Recording it used to require launching the CREATE wizard and choosing "keep
 * the firm's site" — a create flow, to record that nothing should be created.
 * The endpoint is gated Brokers.Update, which is broader than the wizard route
 * that was its only door, so this is the affordance catching up with the
 * permission. It gates NOTHING: inheritance resolves identically stamped or not.
 */
function ConfirmInheritanceAction({
  brokerId,
  alreadyConfirmedAt,
}: {
  brokerId: string;
  alreadyConfirmedAt: string | null;
}) {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const confirm = useConfirmPrequalSiteInheritance(brokerId);

  if (!hasPermission("Brokers.Update")) return null;
  // Guard on a real id: an empty one would POST to /brokers//… and 404 in a way
  // that reads like a server fault rather than a caller bug.
  if (!brokerId) return null;

  return (
    <Button
      size="sm"
      variant="text"
      disabled={confirm.isPending}
      data-testid="prequal-site-confirm-inheritance"
      onClick={() =>
        confirm.mutate(undefined, {
          onSuccess: () => toast.success("Inheritance review recorded."),
          onError: (err) => toast.error(extractErrorMessage(err)),
        })
      }
    >
      {alreadyConfirmedAt ? "Record another review" : "I've reviewed this"}
    </Button>
  );
}

// ── Brokerage ────────────────────────────────────────────────────────────────

function BrokeragePanel({
  brokerageId,
  brokerageName,
}: {
  brokerageId: string;
  brokerageName: string;
}) {
  const query = useBrokeragePrequalSite(brokerageId);
  // Held HERE, above the site conditional, because creating a site flips that
  // conditional while the wizard is still open — see CreateSiteWizardMount.
  const [launch, setLaunch] = useState<PrequalSiteLaunchContext | null>(null);

  if (query.isLoading) return <LoadingLine />;
  if (query.isError || !query.data) return <ErrorLine error={query.error} />;

  const data = query.data;
  const site = data.site;

  return (
    <div className="grid gap-space-4" data-testid="prequal-site-tab">
      {site ? (
        <>
          <SiteCardBlock site={site} />
          <SiteWorkspace
            ownerKind="brokerage"
            ownerId={brokerageId}
            brandId={site.brandId}
            seedRows={data.completions}
            seedTotal={data.completionTotal}
            completionsCaption={`These completions are shared across every broker at ${brokerageName} who inherits this site.`}
            draftsCaption={`These in-progress applications are shared across every broker at ${brokerageName} who inherits this site.`}
            attribution={`“${site.displayName}” is pre-selected, so the completion is credited to this firm.`}
            creditedTo="this firm"
          />
        </>
      ) : (
        // A firm never inherits, so there is no third state here.
        <NoSiteState
          ambiguousFirm={false}
          subject="brokerage"
          create={
            <CreatePrequalSiteButton
              onLaunch={() =>
                setLaunch({
                  ownerBrokerageId: brokerageId,
                  ownerBrokerageName: brokerageName,
                })
              }
            />
          }
        />
      )}

      {/* A FOOTER, deliberately below the tab strip: these are other records,
          not this firm's lists, and pinning them above would push the tabs down
          the page on every brokerage. */}
      <MemberOwnSites members={data.membersWithOwnSites} />

      <CreateSiteWizardMount launch={launch} onClose={() => setLaunch(null)} />
    </div>
  );
}

// ── Buyer team (D8) ──────────────────────────────────────────────────────────

/**
 * A buyer team's own prequalification site.
 *
 * The SIMPLEST of the three panels, and structurally so: `Own` or `None`, with
 * no inherited banner, no confirm-inheritance action, no ambiguous-firm state
 * and no member-sites footer. A team owns its site outright — it has no covering
 * organization to inherit from and nothing beneath it running a separate site —
 * so each of those has nothing to render rather than being omitted for brevity.
 *
 * 🔴 THE ONE THING THAT IS DIFFERENT RATHER THAN ABSENT: a completion on this
 * site becomes a PHOENIX BUYER LEAD in this team's queue. Every other branded
 * site is quarantined out of the Buyer CRM, so the attribution line below is not
 * decoration — it is the only place on this tab that says what happens to the
 * people in the list.
 */
function BuyerTeamPanel({ buyerTeamId, teamName }: { buyerTeamId: string; teamName: string }) {
  const query = useBuyerTeamPrequalSite(buyerTeamId);

  if (query.isLoading) return <LoadingLine />;
  if (query.isError || !query.data) return <ErrorLine error={query.error} />;

  const data = query.data;
  const site = data.site;

  // D2a — a team's site is minted WITH the team (BuyerTeamService.CreateAsync),
  // carries the Phoenix skin, and is managed by the platform: the server refuses
  // edit, unpublish and delete for it, so the card is rendered READ-ONLY here
  // rather than offering three buttons that 400. There is no create launch on
  // this panel for the same reason — nothing on it mounts the wizard.
  return (
    <div className="grid gap-space-4" data-testid="prequal-site-tab">
      {site ? (
        <>
          <SiteCardBlock site={site} readOnly />
          <TeamAdvertisementLinks buyerTeamId={buyerTeamId} />
          <SiteWorkspace
            ownerKind="buyerTeam"
            ownerId={buyerTeamId}
            brandId={site.brandId}
            seedRows={data.completions}
            seedTotal={data.completionTotal}
            // Null on both: a team's site is its OWN, never inherited, so these
            // rows belong to no wider population than the record itself. That is
            // exactly the case these captions exist to mark, and marking it
            // anyway would say something untrue.
            completionsCaption={null}
            draftsCaption={null}
            attribution={`“${site.displayName}” is pre-selected, so the completion is credited to ${teamName} — and unlike a partner site, it also creates a buyer lead in this team’s queue.`}
            creditedTo="this team"
          />
        </>
      ) : (
        // A team never inherits and can never be ambiguous about a covering
        // firm, so there is no third state here — and no create button: the
        // site is the platform's to mint, not a staff member's to draw.
        <NoSiteState
          ambiguousFirm={false}
          subject="buyerTeam"
          create={
            <p className="m-0 text-caption text-gray-mid" data-testid="prequal-site-team-auto">
              A site is created automatically when the team is created; run the
              migration/backfill if this team has none.
            </p>
          }
        />
      )}
    </div>
  );
}

/**
 * The team's advertisements, each with its own per-ad link — `/buy/{slug}?ad=…`,
 * composed server-side (`linkPath`) so the token never has to be assembled here.
 * Rendered beneath the site card because the link is a property of the SITE
 * (the slug) as much as of the ad. The field arrives from the advertising DTO;
 * an ad with no link (no team site yet) shows its name and no row.
 */
function TeamAdvertisementLinks({ buyerTeamId }: { buyerTeamId: string }) {
  const ads = useAdvertisements(buyerTeamId);
  const rows = (ads.data ?? []).map((ad) => ({
    id: ad.id,
    name: ad.name,
    linkPath: (ad as { linkPath?: string | null }).linkPath ?? null,
  }));

  if (ads.isLoading || rows.length === 0) return null;

  return (
    <section
      className="rounded-lg border border-border-light bg-white-pure p-space-4"
      data-testid="prequal-site-ad-links"
    >
      <h3 className="font-display font-bold text-lead text-onyx m-0">Advertisement links</h3>
      <p className="mt-space-1 mb-0 text-caption text-gray-mid">
        Each advertisement carries its own landing link, so a lead is credited to
        the ad it came from as well as to this team.
      </p>
      <ul className="m-0 mt-space-2 list-none p-0 grid gap-space-2">
        {rows.map((ad) => (
          <li key={ad.id} data-testid="prequal-site-ad-link">
            <span className="text-small font-semibold text-onyx">{ad.name}</span>
            {ad.linkPath ? (
              <PublicLinkRow
                path={ad.linkPath}
                label={null}
                copyLabel={`Copy landing link for ${ad.name}`}
                testId={`prequal-site-ad-link-row-${ad.id}`}
              />
            ) : (
              <span className="ml-space-2 text-caption text-gray-mid">No link yet</span>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Member brokers who run their own separate site. These sites are private from
 *  the firm in the PORTAL; staff are exempt, and from the firm's own page this is
 *  the only place they are visible at all. Still no completions and no counts —
 *  that half of the privacy posture is unchanged. */
function MemberOwnSites({ members }: { members: BrokerageMemberOwnSite[] }) {
  const { hasPermission } = useAuth();
  const canManageBrands = hasPermission("Partners.ManageBrands");
  if (members.length === 0) return null;
  return (
    <section
      className="rounded-lg border border-border-light bg-white-pure p-space-4"
      data-testid="prequal-site-member-sites"
    >
      <h3 className="mb-space-2 font-display font-bold text-small uppercase tracking-wider text-gold-shadow">
        Brokers running their own site
      </h3>
      <p className="mb-space-2 text-caption text-gray-mid">
        These brokers do not use this firm&rsquo;s site. Their completions are
        private to them and are not shown here.
      </p>
      <ul className="flex flex-col gap-space-1 text-small">
        {members.map((m) => (
          <li key={m.brokerId}>
            <Link to={`/brokers/${m.brokerId}`} className="text-gold-shadow hover:underline">
              {m.brokerName}
            </Link>
            <span className="text-gray-mid"> — /prequalify/{m.slug}</span>
            {/* The natural next click, now that Partners.ManageBrands reaches
                the C-series roles who actually work these records. */}
            {canManageBrands && (
              <>
                {" · "}
                <Link
                  to={`/partners/brands/${m.brandId}`}
                  className="text-gold-shadow hover:underline"
                >
                  View site
                </Link>
              </>
            )}
          </li>
        ))}
      </ul>
    </section>
  );
}

// ── The sub-tabbed workspace ─────────────────────────────────────────────────

/**
 * The three lists behind one tab bar, plus the single action that starts a new
 * prequalification.
 *
 * 🔴 PERMISSION GATING (D11), and this fixes a live defect. The tab is gated
 * Brokers.Read / Brokerages.Read — held by A1, E1, E2 and a dozen more — while
 * the invitations and in-progress reads are gated Prequalification.Read, held by
 * four. An Underwriter opening a broker record therefore watched the site card
 * and completions render while both lists 403'd. The two sub-tabs are now
 * ABSENT for them rather than present-and-broken; completions rides the same
 * permission as the record, so it stays. Access is deliberately NOT widened —
 * those lists carry applicant names, emails and partial financial applications.
 *
 * An absent tab must also be unreachable by URL, so the recognised `?view=` set
 * is derived from the VISIBLE tabs and anything else falls back to completions.
 */
function SiteWorkspace({
  ownerKind,
  ownerId,
  brandId,
  seedRows,
  seedTotal,
  completionsCaption,
  draftsCaption,
  attribution,
  creditedTo,
}: {
  ownerKind: "broker" | "brokerage" | "buyerTeam";
  ownerId: string;
  brandId: string;
  seedRows: PrequalSiteCompletionRow[];
  seedTotal: number;
  completionsCaption: string | null;
  draftsCaption: string | null;
  /** Who a prequalification started from here is credited to, as a sentence. */
  attribution: string;
  /** The same subject as a noun phrase, for the "you cannot pick a site" case. */
  creditedTo: string;
}) {
  const { hasPermission } = useAuth();
  const [params, setParams] = useSearchParams();
  const canReadPrequal = hasPermission("Prequalification.Read");

  // Completions always; the other two only for a caller whose reads would
  // actually be answered.
  const tabs = canReadPrequal ? VIEW_TABS.map((t) => ({ ...t })) : [{ ...VIEW_TABS[0] }];

  // 🔴 `?view=` is the SINGLE SOURCE OF TRUTH — no mirrored useState, so a Back
  // press or a pasted deep link cannot disagree with what is painted. An absent,
  // unrecognised, or permission-hidden value resolves to Completions rather than
  // erroring: a bad `?view=` is a typo, not a failure.
  const raw = params.get("view");
  const view: PrequalSiteView = tabs.some((t) => t.id === raw)
    ? (raw as PrequalSiteView)
    : "completions";

  // COPY the current params and touch one key, so `?tab=prequalification` (owned
  // by the detail page, which does exactly the same thing in the other
  // direction) and every other param survive.
  const setView = (next: string) => {
    const updated = new URLSearchParams(params);
    updated.set("view", next);
    setParams(updated, { replace: true });
  };

  return (
    <div className="flex flex-col gap-space-3">
      {/* Exactly ONE action on this row. The destructive site controls live on
          the pinned card above; this is the only thing here that creates
          something. Mirrors the partner portal's own prequalification tab, where
          the same button sits above the same three tabs. */}
      <NewPrequalificationAction
        brandId={brandId}
        attribution={attribution}
        creditedTo={creditedTo}
      />

      <Tabs
        tabs={tabs}
        activeId={view}
        onChange={setView}
        ariaLabel="Prequalification lists"
      >
        {/* Only the ACTIVE panel mounts — no co-mounting, which is also why
            there are no counts on the tab labels (D8): an unmounted tab does
            not know its own total, and a stale badge is worse than none. */}
        {view === "completions" && (
          <PrequalSiteCompletionsPanel
            ownerKind={ownerKind}
            ownerId={ownerId}
            seedRows={seedRows}
            seedTotal={seedTotal}
            caption={completionsCaption}
          />
        )}
        {view === "invitations" && (
          <PrequalSiteInvitesSection
            ownerKind={ownerKind}
            ownerId={ownerId}
            brandId={brandId}
          />
        )}
        {view === "in-progress" && (
          <PrequalSiteDraftsSection
            ownerKind={ownerKind}
            ownerId={ownerId}
            brandId={brandId}
            caption={draftsCaption}
          />
        )}
      </Tabs>
    </div>
  );
}

/**
 * "New Prequalification" — the ONE creating action on this tab.
 *
 * It replaces the separate "Send invitation" button and the in-person launcher
 * that stood beside it: both minted an invite against this site and differed
 * only in whether anything was emailed, so they are one button opening the
 * shared two-step wizard (WHO, then HOW). Nothing about the form is
 * re-implemented — the wizard's in-person path mints a SUPPRESSED invite (the
 * client is in the room), creates the draft and hands back its id, and we go
 * straight to the existing staff assist route, which renders the real funnel.
 *
 * That route lives under /deal-flow so it stays in the staff bundle — main.tsx
 * routes anything starting with /prequalify to the isolated portal app.
 *
 * The two states where the button is ABSENT each say why, rather than leaving a
 * gap: a caller who cannot pick a partner site would produce a prequalification
 * credited to the Phoenix-native funnel instead of this partner, and a caller
 * without Prequalification.Manage would only earn a 403. (The third reason —
 * no site at all — is answered by the empty state, which is all this tab renders
 * in that case.)
 */
function NewPrequalificationAction({
  brandId,
  attribution,
  creditedTo,
}: {
  brandId: string;
  attribution: string;
  creditedTo: string;
}) {
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const canStart = hasPermission("Prequalification.Manage");
  // The site picker inside the wizard is fed by the partner-brand list, which is
  // gated separately. Without it the picker offers the Phoenix-native site only,
  // so this partner's site cannot be chosen at all.
  const canReadBrands = hasPermission("Partners.ManageBrands");
  const [open, setOpen] = useState(false);

  // Nothing actionable for a caller who would only earn a 403.
  if (!canStart) return null;

  if (!canReadBrands) {
    return (
      <p className="m-0 text-small text-gray-mid" data-testid="prequal-new-no-brand-access">
        You can start a prequalification, but you cannot choose a partner site,
        so one started here would not be credited to {creditedTo}. Ask an
        administrator to start it.
      </p>
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-space-3">
      <Button size="sm" onClick={() => setOpen(true)} data-testid="prequal-new">
        <UserPlus size={14} strokeWidth={1.5} aria-hidden className="mr-space-1" />
        New Prequalification
      </Button>
      <p className="m-0 text-caption text-gray-mid" data-testid="prequal-new-attribution">
        {attribution}
      </p>

      <SendPrequalInviteModal
        open={open}
        onClose={() => setOpen(false)}
        canReadBrands={canReadBrands}
        // A default, not a lock — the picker stays editable.
        defaultPartnerBrandId={brandId}
        variant="wizard"
        // Straight into the real funnel — the assist route renders it. Required
        // on this variant: a wizard that opens a draft and then strands it is
        // worse than a failure, because nothing looks wrong.
        onStarted={(draftId) => navigate(`/deal-flow/prequalify/assist/${draftId}`)}
      />
    </div>
  );
}

// ── Shared blocks ────────────────────────────────────────────────────────────

/**
 * The CREATE wizard, mounted ONCE per panel as a SIBLING of the `site ? … : …`
 * conditional rather than inside a branch of it.
 *
 * 🔴 THE PLACEMENT IS THE POINT, and it fixes a live defect. Step 1 of the
 * wizard persists a real brand row and invalidates this panel's site read, so
 * `site` flips from absent to present WHILE the wizard is open. Every launcher
 * sits in a branch that flip unmounts — the empty state on both panels, and the
 * inherited create-or-confirm row on the broker one — so a wizard mounted beside
 * its button was torn down on its own success path: the modal vanished after
 * step 1 with no error, the row already written. Modal portals to <body>, but a
 * portal does not outlive the component that owns it.
 *
 * The launch state is a CONTEXT rather than a bare boolean because the wizard is
 * launched for different owners from three positions, and the owner has to
 * travel with the intent. Holding it here also means the launcher stays a plain
 * button that reports intent — see CreatePrequalSiteButton, which is what keeps
 * the whole affordance behind Partners.ManageBrands.
 */
function CreateSiteWizardMount({
  launch,
  onClose,
}: {
  launch: PrequalSiteLaunchContext | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  return (
    <PartnerBrandWizardModal
      open={launch !== null}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
      mode="create"
      ownerBrokerId={launch?.ownerBrokerId}
      ownerBrokerName={launch?.ownerBrokerName ?? undefined}
      ownerBrokerageId={launch?.ownerBrokerageId}
      ownerBrokerageName={launch?.ownerBrokerageName ?? undefined}
      onCompleted={() => {
        onClose();
        // Carried over from the launcher: the record's own site read is exactly
        // what has just changed, so it is refreshed in place rather than
        // navigated away from. The refresh is what flips the branch above — and
        // it is now safe precisely because this mount is not in that branch.
        void invalidatePartnerBrandAndSiteReads(qc);
      }}
    />
  );
}

/** `readOnly` (D2a): a buyer team's site renders no Edit / Unpublish / Delete
 *  whatever the caller's permissions — the server refuses all three — and says
 *  why in a caption instead. Both public links keep their Copy. */
function SiteCardBlock({ site, readOnly = false }: { site: PrequalSiteCard; readOnly?: boolean }) {
  const { hasPermission } = useAuth();
  const canManageBrands = hasPermission("Partners.ManageBrands") && !readOnly;

  return (
    <section
      className="rounded-lg border border-border-light bg-white-pure p-space-4"
      data-testid="prequal-site-card"
    >
      <div className="flex flex-wrap items-start justify-between gap-space-3">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-space-2">
            <Globe size={14} strokeWidth={1.5} aria-hidden />
            <h3 className="font-display font-bold text-h4 text-onyx">{site.displayName}</h3>
            <Badge status={site.isActive ? "finalized" : "archived"}>
              {site.isActive ? "Active" : "Inactive"}
            </Badge>
            <Badge status="info">{site.referenceCode}</Badge>
          </div>
          <SitePublicLinks site={site} />
          {readOnly && (
            <p className="mt-space-2 mb-0 text-caption text-gray-mid" data-testid="prequal-site-managed-note">
              Phoenix-branded · managed automatically
            </p>
          )}
          {!site.isActive && (
            <p className="mt-space-2 mb-0 text-caption text-gray-mid">
              {/* Plural once a team card carries two links, and true of both:
                  the brand resolver filters on IsActive, so an inactive slug
                  resolves to nothing on either route and each serves the
                  Phoenix-native page — at the requested URL, not a redirect. */}
              {site.buyPublicPath
                ? "Inactive — both public links fall back to the Phoenix pages."
                : "Inactive — the public link falls back to the Phoenix funnel."}
            </p>
          )}
        </div>
        {canManageBrands && <SiteControls site={site} />}
      </div>
    </section>
  );
}

/**
 * Every public URL this site publishes, one row each.
 *
 * MOST sites publish exactly one — the prequalification funnel. A BUYER-TEAM
 * site publishes a second: the branded ad landing page at `/buy/{slug}`. That
 * route resolves for a team-owned site and no other, which is why
 * `buyPublicPath` is null everywhere else and why this renders the single row it
 * always did there — a broker card offering the link would advertise a URL that
 * serves the Phoenix-native page (see the field's own note on PrequalSiteCard).
 *
 * 🔴 THE VISIBLE LABELS ARE CONDITIONAL ON THERE BEING TWO ROWS, deliberately.
 * A label distinguishes one row from another; with one row there is nothing to
 * distinguish it from, so labelling unconditionally would put new furniture on
 * every broker and brokerage card to answer a question nobody asked there. The
 * COPY BUTTONS' accessible names are unconditional, because two buttons both
 * reading "Copy public link" is an assistive-technology problem wherever both
 * appear, and naming each by purpose costs nothing where only one does.
 */
function SitePublicLinks({ site }: { site: PrequalSiteCard }) {
  // `|| null` so an empty string is treated as absent rather than rendering a
  // row whose link goes to the current page.
  const buyPath = site.buyPublicPath || null;

  return (
    <>
      <PublicLinkRow
        path={site.publicPath}
        label={buyPath ? "Prequalification site" : null}
        copyLabel="Copy prequalification link"
        testId="prequal-site-public-link"
      />
      {buyPath ? (
        <>
          <PublicLinkRow
            path={buyPath}
            label="Ad landing page"
            copyLabel="Copy ad landing page link"
            testId="prequal-site-buy-link"
          />
          {/* Both facts are ones a staff member would otherwise have to discover
              by clicking. The FONTS half is here because the page deliberately
              self-hosts one family and makes no third-party font request, so a
              site whose brand names its own fonts is not getting them — and
              finding that out from a live ad is the wrong moment. */}
          <p
            className="mt-space-1 mb-0 text-caption text-gray-mid"
            data-testid="prequal-site-buy-note"
          >
            {site.usesPhoenixSkin
              ? // D2a — the slug keeps attribution; the page itself is Phoenix's.
                "Leads captured here land in this team’s queue. Both links serve the Phoenix-branded pages — the site’s slug is what credits the lead to this team."
              : "Leads captured here land in this team’s queue. The page carries this site’s colors, logo and copy — but not its fonts, which stay Phoenix’s own so an ad click loads one self-contained document with no third-party request."}
          </p>
        </>
      ) : null}
    </>
  );
}

/** One public URL, with a copy affordance that tells the truth.
 *
 *  Each row owns its OWN `copied` / `copyFailed` pair — which is the whole reason
 *  this is a component rather than a repeated block. Copying one URL must not
 *  flip the other row's confirmation, or the tick appears beside a link that was
 *  never copied.
 *
 *  Previously this reported success unconditionally: the write was optional-chained
 *  (`navigator.clipboard?.writeText?.(url)`) so a missing Clipboard API no-opped
 *  silently while the button still flipped to "Copied", and nothing ever reset it.
 *  The API is also unavailable on an insecure origin, which is exactly where a
 *  staff member is most likely to be testing. The invitations panel's per-row copy
 *  button carries the same fix. */
function PublicLinkRow({
  path,
  label,
  copyLabel,
  testId,
}: {
  /** App-relative, as the server composed it — e.g. `/prequalify/acme`. */
  path: string;
  /** Which URL this is. Null when the card renders only one — see above. */
  label: string | null;
  /** The copy button's accessible name, distinct per row. */
  copyLabel: string;
  testId: string;
}) {
  const [copied, setCopied] = useState(false);
  const [copyFailed, setCopyFailed] = useState(false);
  const publicUrl =
    typeof window === "undefined" ? path : `${window.location.origin}${path}`;

  // Reset so the confirmation is a transient acknowledgement rather than a
  // permanent label. Cleared on unmount so a late timer cannot set state on a
  // torn-down component.
  useEffect(() => {
    if (!copied && !copyFailed) return;
    const timer = setTimeout(() => {
      setCopied(false);
      setCopyFailed(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [copied, copyFailed]);

  const copy = async () => {
    const write = navigator.clipboard?.writeText;
    if (!write) {
      setCopyFailed(true);
      return;
    }
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
    } catch {
      // Denied permission, or an insecure origin. Say so — a silent failure here
      // means a staff member pastes whatever was on the clipboard before.
      setCopyFailed(true);
    }
  };

  return (
    <div
      className="mt-space-2 flex flex-wrap items-center gap-space-2 text-small"
      data-testid={testId}
    >
      {label ? <span className="text-caption text-gray-mid">{label}</span> : null}
      <a
        href={path}
        target="_blank"
        rel="noreferrer"
        className="text-gold-shadow hover:underline break-all"
      >
        {publicUrl}
      </a>
      <Button size="sm" variant="text" aria-label={copyLabel} onClick={() => void copy()}>
        {copied ? (
          <>
            <Check size={14} strokeWidth={1.5} aria-hidden /> Copied
          </>
        ) : (
          <>
            <Copy size={14} strokeWidth={1.5} aria-hidden /> Copy link
          </>
        )}
      </Button>
      {copyFailed && (
        <span className="text-caption text-status-error" role="alert">
          Couldn&rsquo;t copy — select the link and copy it manually.
        </span>
      )}
    </div>
  );
}

/**
 * Edit / unpublish / delete. All three self-gate on Partners.ManageBrands via the
 * caller, so a viewer without it sees the card and none of the controls.
 *
 * UNPUBLISH is the soft path and is presented as the obvious one: it reuses the
 * existing active toggle, keeps every reference intact, and is reversible in a
 * click. DELETE is destructive and, by server rule, available only for a site
 * that was created and never used — see DeleteSiteAction.
 */
function SiteControls({ site }: { site: PrequalSiteCard }) {
  const qc = useQueryClient();
  const toast = useToast();
  const [editOpen, setEditOpen] = useState(false);
  const setActive = useSetPartnerBrandActive(site.brandId);

  const toggleActive = () =>
    setActive.mutate(!site.isActive, {
      onSuccess: () =>
        toast.success(
          site.isActive
            ? "Site unpublished — the public link now falls back to the Phoenix funnel."
            : "Site published.",
        ),
      onError: (err) => toast.error(extractErrorMessage(err)),
    });

  return (
    <div className="flex flex-wrap items-center gap-space-2">
      <Button size="sm" variant="secondary" onClick={() => setEditOpen(true)}>
        <Pencil size={14} strokeWidth={1.5} aria-hidden /> Edit
      </Button>
      <Button
        size="sm"
        variant="secondary"
        disabled={setActive.isPending}
        onClick={toggleActive}
        data-testid="prequal-site-toggle-active"
      >
        {site.isActive ? "Unpublish" : "Publish"}
      </Button>
      <DeleteSiteAction site={site} />

      {/* Opens in place — the whole point of the popup conversion is that a staff
          member never leaves the record they launched from.

          This is the EDIT mount, and it is NOT the only wizard on the panel: the
          CREATE mount is hoisted to the panel root (CreateSiteWizardMount), so
          wherever a site exists both are mounted and each holds its own open
          state. Harmless — a closed Modal renders null — and deliberate. The two
          used to sit in mutually exclusive branches, and that exclusivity was not
          a safety property but the defect: it is what let a create flow tear its
          own wizard down the moment it produced the site. */}
      <PartnerBrandWizardModal
        open={editOpen}
        onOpenChange={setEditOpen}
        mode="edit"
        brandId={site.brandId}
        onCompleted={() => {
          setEditOpen(false);
          void invalidatePartnerBrandAndSiteReads(qc);
        }}
      />
    </div>
  );
}

/**
 * Delete, with the refusal stated before anything destructive is offered.
 *
 * The server refuses whenever ANYTHING references the brand — an inheriting
 * broker, or any submission, invite or draft — so this is genuinely available
 * only for a site created and never used. It NAMES the inheriting brokers,
 * because a count cannot tell a staff member the difference between "one broker
 * loses their site" and "this site has forty completions".
 *
 * The message is rendered VERBATIM from the server. Composing a second one here
 * would let the warning shown before the click and the error shown after it drift
 * apart, which is precisely the failure a pre-check exists to prevent.
 */
function DeleteSiteAction({ site }: { site: PrequalSiteCard }) {
  const toast = useToast();
  const [open, setOpen] = useState(false);
  // Only asked once the staff member reaches for delete — the tab does not spend
  // a request per load on a question nobody has posed.
  const eligibility = usePartnerBrandDeleteEligibility(open ? site.brandId : undefined);
  const remove = useDeletePartnerBrand(site.brandId);

  const canDelete = eligibility.data?.canDelete === true;
  const loading = eligibility.isLoading;

  return (
    <>
      <Button
        size="sm"
        variant="text"
        onClick={() => setOpen(true)}
        data-testid="prequal-site-delete"
      >
        <Trash2 size={14} strokeWidth={1.5} aria-hidden /> Delete
      </Button>

      <ConfirmModal
        open={open}
        onCancel={() => setOpen(false)}
        title={`Delete ${site.displayName}?`}
        destructive={canDelete}
        // A refused delete offers no destructive action at all — the only thing
        // left to do is read why and close.
        confirmLabel={canDelete ? "Delete site" : "Understood"}
        onConfirm={() => {
          if (!canDelete) {
            setOpen(false);
            return;
          }
          remove.mutate(undefined, {
            onSuccess: () => {
              toast.success("Prequalification site deleted.");
              setOpen(false);
            },
            onError: (err) => toast.error(extractErrorMessage(err)),
          });
        }}
        message={
          <div className="grid gap-space-2" data-testid="prequal-site-delete-body">
            {loading && <p className="m-0 text-gray-mid italic">Checking what uses this site…</p>}

            {eligibility.isError && (
              <p className="m-0 text-status-error" role="alert">
                {extractErrorMessage(eligibility.error)}
              </p>
            )}

            {eligibility.data && !canDelete && (
              <>
                {/* Verbatim. It names which blocker was hit, how many, and which
                    brokers — and is the same string the refused DELETE returns.
                    Composing a second message here would let the warning shown
                    before the click and the error shown after it drift apart. */}
                <p className="m-0" role="alert" data-testid="prequal-site-delete-refusal">
                  {eligibility.data.refusalMessage}
                </p>
              </>
            )}

            {eligibility.data && canDelete && (
              <p className="m-0">
                Nothing references this site — no completions, invitations or
                in-progress applications, and no broker inherits it. Deleting it
                frees the owner to be given a new one.
              </p>
            )}
          </div>
        }
      />
    </>
  );
}

/** The empty state: nothing resolved for this record, by ownership or — for a
 *  broker — by inheritance. There is deliberately no fallback and no sample
 *  content, because a record either resolves a site or it does not, and the
 *  create action is the only answer to the second. How MANY records are in that
 *  state is a property of the data rather than of this component, so it is not
 *  asserted here.
 *
 *  It also carries the retired launcher's "no site" reason: with nothing to
 *  credit a completion to, starting one is not offered rather than producing a
 *  prequalification credited to nobody. */
function NoSiteState({
  ambiguousFirm,
  subject,
  create,
}: {
  ambiguousFirm: boolean;
  subject: "broker" | "brokerage" | "buyerTeam";
  create: ReactNode;
}) {
  return (
    <div data-testid="prequal-site-empty">
      <EmptyState
        icon={Globe}
        title="No prequalification site"
        description={
          ambiguousFirm
            ? "This broker is affiliated with more than one active brokerage and none is marked primary, so no firm site can be inherited. Set a primary affiliation on the Brokerages tab, or give this broker their own site."
            : subject === "broker"
              ? "This broker has no site of their own and no covering brokerage with one."
              : subject === "brokerage"
                ? "This firm has no shared prequalification site yet."
                : "This team has no prequalification site yet. A team owns its site outright — there is nothing for it to inherit."
        }
      >
        <p
          className="mt-space-2 mb-0 text-caption text-gray-mid"
          data-testid="prequal-site-empty-no-start"
        >
          Nothing can be credited to a site that does not exist, so
          invitations, in-progress applications and starting one in person all
          become available once there is one.
        </p>
        <div className="mt-space-4">{create}</div>
      </EmptyState>
    </div>
  );
}

function LoadingLine() {
  return <p className="text-small text-gray-mid italic">Loading prequalification site…</p>;
}

function ErrorLine({ error }: { error: unknown }) {
  return (
    <p className="text-small text-status-error" role="alert">
      {error instanceof Error ? error.message : "Could not load the prequalification site."}
    </p>
  );
}
